<template>
  <div class="irp-contacts-count-cell">
    <template v-if="contactGroups.length">
      <BittsPopover
        v-for="group in contactGroups"
        :key="group.type"
        trigger="hover"
        placement="bottomLeft"
        :mount-to-body="true"
      >
        <Component
          :is="contactsRoute ? RouterLink : 'span'"
          v-bind="linkBinds(group)"
          class="irp-contacts-count-cell__tag"
          :data-testid="`irp-contacts-count-${group.type}-tag`"
        >
          <BittsTag
            :text="`${group.contacts.length} ${group.label}`"
            color="neutral"
            size="x-small"
          />
        </Component>
        <template #content>
          <ul
            class="irp-contacts-count-cell__list ph-no-capture dd-privacy-mask"
            :data-testid="`irp-contacts-count-${group.type}-list`"
          >
            <li
              v-for="(contact, index) in group.contacts"
              :key="index"
              class="irp-contacts-count-cell__contact"
            >
              <span class="irp-contacts-count-cell__name">{{
                contact.name
              }}</span>
              <span
                v-if="contact.title"
                class="irp-contacts-count-cell__detail"
              >
                {{ contact.title }}
              </span>
              <span
                v-if="contact.email"
                class="irp-contacts-count-cell__detail"
              >
                {{ contact.email }}
              </span>
            </li>
          </ul>
        </template>
      </BittsPopover>
    </template>
    <span v-else class="irp-contacts-count-cell__empty">&mdash;</span>
  </div>
</template>

<script setup lang="ts">
import { BittsPopover, BittsTag } from '@crossbeam/bitts';

import type { ICellRendererParams } from '@ag-grid-community/core';
import { pluralize } from 'humanize-plus';
import { computed } from 'vue';
import { RouterLink } from 'vue-router';

import type { RecordOverviewPartnerType } from '@/types/individual_record';

import {
  EXPAND_ALL_QUERY,
  IRP_TAB,
  IRP_TAB_ROUTES,
  addToCrmContactFromRecordContact,
} from './irp';
import { useRecordRouteParams } from './useRecordRouteParams';

const { params } = defineProps<{
  params: ICellRendererParams<RecordOverviewPartnerType>;
}>();

const recordParams = useRecordRouteParams();

const contacts = computed(() => params.data?.contacts?.items ?? []);

function summarizeContacts(isMissingFromCrm: boolean) {
  return contacts.value
    .filter((contact) => contact.missing_from_crm === isMissingFromCrm)
    .map((contact) => {
      const { firstName, lastName, email, title } =
        addToCrmContactFromRecordContact(contact);
      return {
        name: [firstName, lastName].filter(Boolean).join(' ') || email,
        title,
        email,
      };
    });
}

const contactGroups = computed(() =>
  [
    { type: 'common', label: 'Common', contacts: summarizeContacts(false) },
    { type: 'new', label: 'New', contacts: summarizeContacts(true) },
  ].filter((group) => group.contacts.length),
);

type ContactGroupType = (typeof contactGroups.value)[number];

const contactsRoute = computed(() => {
  const partnerOrgId = params.data?.partner_organization_id;
  if (!partnerOrgId) return null;
  return {
    name: IRP_TAB_ROUTES[IRP_TAB.contacts],
    params: recordParams.value,
    query: { partner_org_ids: String(partnerOrgId), ...EXPAND_ALL_QUERY },
  };
});

function linkBinds(group: ContactGroupType) {
  if (!contactsRoute.value) return {};
  const count = group.contacts.length;
  return {
    to: contactsRoute.value,
    'aria-label': `View ${count} ${group.type} ${pluralize(count, 'contact')} with ${params.data?.partner_name ?? ''}`,
  };
}
</script>

<style lang="pcss">
@reference "../../../css/theme.css";
.irp-contacts-count-cell {
  @apply flex items-center h-full w-full;
}

.irp-contacts-count-cell__tag {
  @apply flex items-center rounded-4;
}

a.irp-contacts-count-cell__tag:focus-visible {
  @apply outline-hidden outline-2 outline-neutral-border-focus;
}

.irp-contacts-count-cell__list {
  @apply flex max-h-[240px] flex-col gap-8 overflow-y-auto p-8;
}

.irp-contacts-count-cell__contact {
  @apply flex flex-col text-sm;
}

.irp-contacts-count-cell__name {
  @apply font-bold text-neutral-text-strong;
}

.irp-contacts-count-cell__detail {
  @apply text-neutral-text-weak;
}

.irp-contacts-count-cell__empty {
  @apply text-neutral-text-weak;
}

.irp-contacts-count-cell .bitts-tag .ant-tag {
  @apply mr-4 whitespace-nowrap;
}
</style>
